import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * ProgrammePageShell
 * Shared layout wrapper for all 4 programme detail pages.
 * Provides: Hero banner, breadcrumb, page scroll restore, and CTA footer.
 * Children = the page-specific content.
 */
const ProgrammePageShell = ({ title, subtitle, icon: Icon, heroImage, breadcrumb, children }) => {
  // Scroll to top on mount
  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
  };

  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">

      {/* ── Hero Banner ───────────────────────────────────────────────── */}
      <section
        className="relative pt-28 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e293b 0%, #800000 60%, #450000 100%)' }}
      >
        {/* Decorative blobs */}
        <div aria-hidden className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div aria-hidden className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 relative z-10">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-white/60 mb-8 flex-wrap"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/programme" className="hover:text-white transition-colors">Programme</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">{breadcrumb}</span>
          </motion.nav>

          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="max-w-3xl"
          >
            {/* Icon badge */}
            {/* {Icon && (
              <motion.div variants={fadeUp} className="inline-flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl mb-6">
                <Icon className="w-7 h-7 text-white" />
              </motion.div>
            )}
            <motion.h1
              variants={fadeUp}
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {title}
            </motion.h1> */}
            <motion.div variants={fadeUp} className="flex flex-row items-center gap-4 mb-4">
              {Icon && (
                <div className="flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shrink-0">
                  <Icon className="w-7 h-7 text-white" />
                </div>
              )}
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {title}
              </h1>
            </motion.div>
            <motion.p variants={fadeUp} className="text-lg text-white/75 leading-relaxed">
              {subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Page Content ─────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        {children}
      </div>

      {/* ── CTA Section ──────────────────────────────────────────────── */}
      {/* <section className="bg-slate-900 py-16 mt-auto">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Start Your Transformation?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Our expert team at GastroCare Bhopal is here to guide you every step of the way. Book a consultation today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#800000] hover:bg-white hover:text-[#800000] text-white font-bold px-8 py-4 rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              Book Appointment
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:+917554055123"
              className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white text-white font-bold px-8 py-4 rounded-full transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default ProgrammePageShell;
